"use client";

import { memo, useCallback, useEffect, useId, useState } from "react";
import {
  useA2UIComponent,
  type A2UIComponentProps,
  type Types,
} from "@a2ui/react";
import { resolveEnum } from "./utils";

export const BeeOSTextField = memo(function BeeOSTextField({
  node,
  surfaceId,
}: A2UIComponentProps<Types.TextFieldNode>) {
  const { resolveString, setValue, getValue } = useA2UIComponent(
    node,
    surfaceId,
  );
  const props = node.properties;
  const id = useId();
  const label = resolveString(props.label);
  const textPath = props.text?.path;
  // For path-bound fields, bypass resolveString (which has a bug in @a2ui/react
  // v0.8 where getData returning undefined becomes the string "undefined").
  // Instead, read directly from the data model with a proper null guard.
  const initialValue = textPath
    ? (() => { const v = getValue(textPath); return v != null ? String(v) : ""; })()
    : (resolveString(props.text) ?? "");
  const fieldType = resolveEnum<string>(props.type, "shortText");
  const validationRegexp = props.validationRegexp;

  const [value, setLocalValue] = useState(initialValue);
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (textPath) {
      const externalValue = getValue(textPath);
      if (externalValue != null && String(externalValue) !== value) {
        setLocalValue(String(externalValue));
      }
    }
  }, [textPath, getValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.text?.literalString !== undefined) {
      setLocalValue(props.text.literalString);
    }
  }, [props.text?.literalString]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      if (validationRegexp) {
        try {
          setIsValid(new RegExp(validationRegexp).test(newValue));
        } catch {
          setIsValid(true);
        }
      }
      if (textPath) setValue(textPath, newValue);
    },
    [textPath, setValue, validationRegexp],
  );

  const inputType =
    fieldType === "number"
      ? "number"
      : fieldType === "date"
        ? "date"
        : "text";
  const isTextArea = fieldType === "longText";

  const hostStyle: React.CSSProperties =
    node.weight !== undefined
      ? ({ "--weight": node.weight } as React.CSSProperties)
      : {};

  const baseInputClass =
    "flex w-full rounded-xl border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.2)]";
  const validClass = "border-border/40 focus-visible:ring-primary/20 focus-visible:border-primary/30 focus-visible:shadow-[inset_0_1px_2px_rgba(0,0,0,0.04),0_0_0_3px_rgba(0,0,0,0.03)] dark:focus-visible:shadow-[inset_0_1px_2px_rgba(0,0,0,0.15),0_0_0_3px_rgba(255,255,255,0.04)]";
  const invalidClass = "border-destructive/60 focus-visible:ring-destructive/20 focus-visible:border-destructive/50";
  const inputClass = `${baseInputClass} ${isValid ? validClass : invalidClass}`;

  return (
    <div className="a2ui-textfield w-full" style={hostStyle}>
      <div className="flex flex-col gap-1.5 w-full min-w-0">
        {label && (
          <label
            htmlFor={id}
            className="text-sm font-medium leading-none text-foreground"
          >
            {label}
          </label>
        )}
        {isTextArea ? (
          <textarea
            id={id}
            value={value}
            onChange={handleChange}
            placeholder="Enter a value..."
            className={`${inputClass} min-h-[80px] resize-y`}
          />
        ) : (
          <input
            type={inputType}
            id={id}
            value={value}
            onChange={handleChange}
            placeholder="Enter a value..."
            className={`${inputClass} h-10`}
          />
        )}
        {!isValid && validationRegexp && (
          <p className="text-xs text-destructive">
            Input does not match the required format
          </p>
        )}
      </div>
    </div>
  );
});
